/* eslint-disable no-constant-condition */
import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import intl from "react-intl-universal"
import { CALC_TYPES, CONTROL_LEVELS } from "../../../../constants"
import styles from "./index.module.scss"
import { Flex, Radio, toast } from "@stardust-ds/react"
import Button from "../../../../../../atoms/Button"
import ModalAutomaticCalc from "../modalAutomaticCalc"
import useDataApi from "../../../../../../../hooks/useDataApi"
import { equipmentAnalyteControlCalc } from "../../../../../../../services/ciq"
import ModalSeeAllRuns from "../modalSeeAllRuns"
import {
  fnisOnlyPermition,
  fnIsAccessPermition,
} from "../../.././../../../../utils/verifyPermitions"

const LevelCards = ({ oAnalyte, fetchReleaseDetails }) => {
  const [oLevelAutomaticCalc, setOLevelAutomaticCalc] = useState(null) // selected level
  const [bAutomaticCalcModalIsOpen, setBAutomaticCalcModalIsOpen] = useState(false)
  const [bSeeAllRunsModalIsOpen, setBSeeAllRunsModalIsOpen] = useState(false)

  if (!oAnalyte) return null

  const { anlt_levels, anlt_type } = oAnalyte

  if (!anlt_levels?.length || anlt_type !== "Quantitativo") return null

  const getLevelProperties = (elevel) => {
    return CONTROL_LEVELS?.find((l) => l.level === elevel)
  }

  const onCloseAutomaticCalcModal = () => {
    setOLevelAutomaticCalc(null)
    setBAutomaticCalcModalIsOpen(false)
  }

  const onCloseSeeAllRunsModal = () => {
    setOLevelAutomaticCalc(null)
    setBSeeAllRunsModalIsOpen(false)
  }

  return (
    <div className={styles.levelCardsContainer}>
      {anlt_levels.map((controlLevel) => (
        <Card
          key={controlLevel.level}
          controlLevel={{ ...controlLevel, ...getLevelProperties(controlLevel.elevel) }}
          setOLevelAutomaticCalc={setOLevelAutomaticCalc}
          openAutomaticCalcModal={() => setBAutomaticCalcModalIsOpen(true)}
          openSeeAllRunsModal={() => setBSeeAllRunsModalIsOpen(true)}
          fetchReleaseDetails={fetchReleaseDetails}
        />
      ))}
      {bAutomaticCalcModalIsOpen && (
        <ModalAutomaticCalc
          fnOnClose={onCloseAutomaticCalcModal}
          oControlLevel={oLevelAutomaticCalc}
          fetchReleaseDetails={fetchReleaseDetails}
        />
      )}
      {bSeeAllRunsModalIsOpen && (
        <ModalSeeAllRuns
          fnOnClose={onCloseSeeAllRunsModal}
          oControlLevel={oLevelAutomaticCalc}
        />
      )}
    </div>
  )
}
export default LevelCards

const RADIO_INACTIVE_COLOR = "#5A646E"
const Card = ({
  controlLevel,
  setOLevelAutomaticCalc,
  openAutomaticCalcModal,
  openSeeAllRunsModal,
  fetchReleaseDetails,
}) => {
  const [selectedCalcMethod, setSelectedCalcMethod] = useState()
  const [oCalcRes, fnCalcReq] = useDataApi(equipmentAnalyteControlCalc)
  const { equipmentId, analyteId } = useParams()

  useEffect(() => {
    if (!controlLevel || selectedCalcMethod) return
    setSelectedCalcMethod(controlLevel.ccalc_type)
  }, [controlLevel, selectedCalcMethod])

  useEffect(() => {
    if (oCalcRes.loading || !oCalcRes.done) return
    if (!oCalcRes.error) return

    toast({
      type: "error",
      title: intl.get("ciq.release_details.levels_cards.change_calc_type_error_title", {
        level: controlLevel.level,
      }),
      description: intl.get(
        "ciq.release_details.levels_cards.change_calc_type_error_description",
      ),
    })

    // reset radio button
    setSelectedCalcMethod(controlLevel.ccalc_type)
  }, [oCalcRes])

  if (!controlLevel) return null

  const { level, color, label, bavailable, launch, manual_average } = controlLevel

  if (!bavailable) {
    return (
      <div className={styles.disabledCard} style={{ borderColor: color }}>
        <p className={styles.levelText} style={{ color }}>
          {intl.get(label)}
        </p>
        <span>-</span>
      </div>
    )
  }

  const aRadiosOptions = [
    {
      label: intl.get("ciq.release_details.levels_cards.radio.calculated"),
      name: `calculated-level-${level}`,
      id: `calculated-level-${level}`,
      value: CALC_TYPES.LEAFLET_VALUE,
    },
    {
      label: intl.get("ciq.release_details.levels_cards.radio.manual"),
      name: `manual-level-${level}`,
      id: `manual-level-${level}`,
      value: CALC_TYPES.MANUAL_AVERAGE,
    },
  ]

  const setLevelAutomaticCalc = () => {
    if (oCalcRes.loading) return
    setOLevelAutomaticCalc(controlLevel)
  }

  const onChangeCalcMethod = async (e) => {
    if (oCalcRes.loading) return

    const value = e?.currentTarget?.value

    if (value === CALC_TYPES.MANUAL_AVERAGE) {
      setManualAverage()
      return
    }

    setSelectedCalcMethod(value)

    await makeCalcReq({
      ctype: value,
    })
  }

  const makeCalcReq = async (body) => {
    return await fnCalcReq(equipmentId, analyteId, controlLevel?.ncontrolid, body)
  }

  const setManualAverage = async () => {
    // active the last manual_average
    if (manual_average && Object?.keys(manual_average)?.length > 0) {
      const body = {
        ctype: CALC_TYPES?.MANUAL_AVERAGE,
        start_run: manual_average?.abetweeen_run?.[0]?.eqpanltrun_nid,
        end_run: manual_average?.abetweeen_run?.[1]?.eqpanltrun_nid,
      }

      if (manual_average?.adisregard_run?.length) {
        body.adisregard_run = manual_average?.adisregard_run.map(
          (run) => run.eqpanltrun_nid,
        )
      }

      setSelectedCalcMethod(CALC_TYPES.MANUAL_AVERAGE)
      await makeCalcReq(body)
      await fetchReleaseDetails()
      return
    }

    // open select runs modal
    handleOnOpenAutomaticCalcModal()
  }

  const handleOnOpenAutomaticCalcModal = () => {
    setLevelAutomaticCalc()
    openAutomaticCalcModal()
  }

  const handleOnOpenSeeAllRunsModal = () => {
    setLevelAutomaticCalc()
    openSeeAllRunsModal()
  }

  const bIsManualAverage = selectedCalcMethod === CALC_TYPES.MANUAL_AVERAGE

  const isAccessCreateEditLaunchn = fnIsAccessPermition("create-edit-launch")
  const isOnlyView = !isAccessCreateEditLaunchn
  const isOnlyAccessModuleCiq = fnisOnlyPermition("access-ciq") || isOnlyView

  return (
    <div className={styles.levelCard} style={{ borderColor: color }}>
      <Flex width="100%" justifyContent="space-between">
        <p className={styles.levelText} style={{ color }}>
          {intl.get(label)}
        </p>
        <Flex gap={12} className={oCalcRes.loading ? styles.cursorWait : ""}>
          {aRadiosOptions.map((radio) => {
            const checked = radio.value === selectedCalcMethod
            return (
              <Flex key={radio.id} gap={8} align="center" justifyContent="center">
                <Radio
                  disabled={isOnlyAccessModuleCiq}
                  checked={checked}
                  onChange={onChangeCalcMethod}
                  inactiveColor={RADIO_INACTIVE_COLOR}
                  activeColor={color}
                  {...radio}
                />
                <label
                  htmlFor={radio.id}
                  className={styles.radioLabel}
                  style={{
                    color: checked ? color : RADIO_INACTIVE_COLOR,
                    fontWeight: checked ? 700 : 600,
                  }}
                >
                  {radio.label}
                </label>
              </Flex>
            )
          })}
        </Flex>
      </Flex>
      <LaunchData launch={launch} manual_average={manual_average} />
      {bIsManualAverage && (
        <Flex width="100%" alignItems="center" justifyContent="space-between" gap={16}>
          <p className={styles.calcDescriptionText}>
            {manual_average?.abetweeen_run?.length > 0 && (
              <>
                {intl.get("ciq.release_details.levels_cards.races_considered")}{" "}
                {getOnlyRunsNumbers(manual_average.abetweeen_run).join(" e ")}
                <br />
              </>
            )}
            {manual_average?.adisregard_run?.length > 0 && (
              <>
                {intl.get("ciq.release_details.levels_cards.races_disregarded")}{" "}
                {getOnlyRunsNumbers(manual_average.adisregard_run).slice(0, 4).join(", ")}{" "}
                <span onClick={handleOnOpenSeeAllRunsModal} style={{ color }}>
                  {intl.get("ciq.release_details.levels_cards.see_more")}
                </span>
              </>
            )}
          </p>

          <Button
            type="button"
            liquid
            className={styles.reCalcButton}
            onClick={handleOnOpenAutomaticCalcModal}
            disabled={oCalcRes.loading}
          >
            {intl.get("ciq.release_details.levels_cards.recalculate")}
          </Button>
        </Flex>
      )}
    </div>
  )
}

const LaunchData = ({ launch, manual_average }) => {
  if (!launch) return null

  const { launch_naverage, launch_nstandard_deviation } = launch
  const nCoefficientVariation = Number(
    (launch_nstandard_deviation / launch_naverage) * 100,
  )

  const coefficientVariationValue =
    launch_nstandard_deviation === 0 || launch_naverage === 0
      ? "-"
      : `${formatNumber(nCoefficientVariation)}%`

  return (
    <>
      <RowValues
        label={intl.get("ciq.release_details.levels_cards.average")}
        calculatedValue={Number(launch_naverage).toLocaleString()}
        manualValue={formatNumber(manual_average?.naverage)}
      />
      <RowValues
        label={intl.get("ciq.release_details.levels_cards.standard_deviation")}
        calculatedValue={Number(launch_nstandard_deviation).toLocaleString()}
        manualValue={formatNumber(manual_average?.nstandard_deviation)}
      />
      <RowValues
        label={intl.get("ciq.release_details.levels_cards.coefficient_variation")}
        calculatedValue={coefficientVariationValue}
        manualValue={`${
          formatNumber(manual_average?.ncoefficient_variation)
            ? `${formatNumber(manual_average?.ncoefficient_variation)}%`
            : formatNumber(manual_average?.ncoefficient_variation)
        }`}
      />
    </>
  )
}

const RowValues = ({ label, calculatedValue, manualValue }) => (
  <Flex width="100%" gap={16}>
    <div style={{ minWidth: 173 }}>
      <p className={styles.valueText} style={{ textAlign: "left" }}>
        {label}
      </p>
    </div>
    <Flex width="100%" gap={8} justifyContent="space-around">
      <p className={styles.valueText}>{calculatedValue}</p>
      <p className={styles.valueText}>{manualValue}</p>
    </Flex>
  </Flex>
)

const formatNumber = (number) => {
  if (typeof number !== "number") return 0
  return number?.toFixed(2)?.replace(".", ",")
}

const getOnlyRunsNumbers = (runs) => {
  return runs?.map((run) => run.eqpanltrun_number)
}
